import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  makeStyles,
  Typography,
} from "@material-ui/core";
import { presentFormatedData } from "../../functionalComponents/dataFormatFunctions";
import moment from "moment";

const useStyles = makeStyles({
  tableContainer: {
    marginTop: "20px",
    border: "1px solid #ccc",
    borderRadius: "8px",
  },
  headerCell: {
    fontWeight: "bold",
    backgroundColor: "#f5f5f5",
    padding: "12px 16px",
  },
  bodyCell: {
    padding: "12px 16px",
    verticalAlign: "top",
  },
  timestamp: {
    fontSize: "11px",
    color: "#666",
    marginTop: "2px",
  },
});

const isPopulated = (val) => val !== null && val !== undefined && val !== "";

const PricingTable = ({ data }) => {
  const classes = useStyles();

  const sl = data?.scheduleLineDetails || {};
  const pricing = data?.pricingDetails || {};

  // Pricing values — check pricingDetails first, fall back to scheduleLineDetails
  const sponsorPrice     = pricing?.netPriceAmount      ?? sl?.netPriceAmount;
  const cmoProposed      = pricing?.netAmountCMO        ?? sl?.netAmountCMO;
  const cmoProposedTs    = pricing?.netAmountCMOUpdatedDate   ?? sl?.netAmountCMOUpdatedDate;
  const cmoInvoice       = pricing?.cmoInvoicePricing         ?? sl?.cmoInvoicePricing;
  const cmoInvoiceTs     = pricing?.cmoInvoiceIngestionDate   ?? sl?.cmoInvoiceIngestionDate;
  const currency         = pricing?.currency            ?? sl?.currency;
  const uom              = pricing?.orderPriceUnit      ?? sl?.orderPriceUnit;
  const conditionPricingUnit = pricing?.netPriceQuantity ?? sl?.netPriceQuantity;

  // CMO Invoice supersedes CMO Proposed once ingested
  const invoiceActive  = isPopulated(cmoInvoice);
  const proposedActive = !invoiceActive && isPopulated(cmoProposed);

  // Active CMO Price for variance
  const activeCMOPrice = invoiceActive ? Number(cmoInvoice) : (proposedActive ? Number(cmoProposed) : null);

  // Variance = ((Active CMO Price − Sponsor PO Price) / Sponsor PO Price) × 100
  let varianceDisplay = "";
  if (
    activeCMOPrice !== null &&
    isPopulated(sponsorPrice) &&
    Number(sponsorPrice) !== 0
  ) {
    const v = ((activeCMOPrice - Number(sponsorPrice)) / Number(sponsorPrice)) * 100;
    varianceDisplay = `${v.toFixed(2)}%`;
  }

  return (
    <TableContainer component={Paper} className={classes.tableContainer}>
      <Table className="default-table-styles">
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>BMS Price per Unit</TableCell>
            <TableCell className={classes.headerCell}>CMO Price per Unit</TableCell>
            <TableCell className={classes.headerCell}>CMO Invoice Pricing</TableCell>
            <TableCell className={classes.headerCell}>Condition Pricing Unit</TableCell>
            <TableCell className={classes.headerCell}>UoM</TableCell>
            <TableCell className={classes.headerCell}>Currency</TableCell>
            <TableCell className={classes.headerCell}>Variance (%)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            {/* Sponsor PO Price — read-only, from SAP extract */}
            <TableCell className={classes.bodyCell}>
              {presentFormatedData(sponsorPrice)}
            </TableCell>

            {/* CMO Proposed Pricing — active when no invoice; inactive shows "-" */}
            <TableCell className={classes.bodyCell}>
              {proposedActive ? (
                <>
                  {presentFormatedData(cmoProposed)}
                  {isPopulated(cmoProposedTs) && moment(cmoProposedTs).isValid() && (
                    <Typography className={classes.timestamp}>
                      {moment(cmoProposedTs).format("DD-MMM-YYYY, HH:mm:ss").toUpperCase()}
                    </Typography>
                  )}
                </>
              ) : (
                ""
              )}
            </TableCell>

            {/* CMO Invoice Pricing — supersedes Proposed once ingested; inactive shows "-" */}
            <TableCell className={classes.bodyCell}>
              {invoiceActive ? (
                <>
                  {presentFormatedData(cmoInvoice)}
                  {isPopulated(cmoInvoiceTs) && moment(cmoInvoiceTs).isValid() && (
                    <Typography className={classes.timestamp}>
                      {moment(cmoInvoiceTs).format("DD-MMM-YYYY, HH:mm:ss").toUpperCase()}
                    </Typography>
                  )}
                </>
              ) : (
                ""
              )}
            </TableCell>

            {/* Condition Pricing Unit — SAP order price unit (price per N units) */}
            <TableCell className={classes.bodyCell}>
              {presentFormatedData(conditionPricingUnit)}
            </TableCell>

            <TableCell className={classes.bodyCell}>
              {presentFormatedData(uom)}
            </TableCell>

            <TableCell className={classes.bodyCell}>
              {presentFormatedData(currency)}
            </TableCell>

            {/* Variance — auto-recalculates when active CMO price or sponsor price changes */}
            <TableCell className={classes.bodyCell}>
              {varianceDisplay}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default PricingTable;
